import * as React from "react";

/**
 * A library card grid that scrolls inside a two-row window and loads the
 * next page as its bottom sentinel comes into view (no "Load more" click).
 * Shared by the Games and Uploaded tabs so both behave the same.
 *
 * Attach `scrollRef` to the scroll container, `gridRef` to the card grid
 * and `sentinelRef` to an element after the grid; give the container
 * `maxHeight: maxHeight`.
 */
export function useInfiniteGrid({
  count,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  /** Cards currently rendered; re-measures when it changes. */
  count: number;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore?: () => void;
}) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const gridRef = React.useRef<HTMLDivElement | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [sentinelVisible, setSentinelVisible] = React.useState(false);

  // Cap the scroll area to exactly two card rows: measure where the third
  // row starts (card height depends on column width, which changes at the
  // sm/lg breakpoints, so this can't be a fixed px value) and clip there.
  // Fewer than three rows means nothing to clip, so the area just grows.
  const [maxHeight, setMaxHeight] = React.useState<number | undefined>(undefined);

  React.useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const cards = Array.from(grid.children) as HTMLElement[];
      // offsetTop is relative to the nearest positioned ancestor, which may
      // be far above this grid (the page, not the grid itself), so the cap
      // has to be the height *between* rows, not a row's raw offsetTop.
      const rowTops = Array.from(new Set(cards.map((c) => c.offsetTop))).sort((a, b) => a - b);
      setMaxHeight(rowTops.length > 2 ? rowTops[2] - rowTops[0] : undefined);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [count]);

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setSentinelVisible(entry.isIntersecting),
      { root: scrollRef.current, threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [count]);

  React.useEffect(() => {
    if (sentinelVisible && hasMore && !loadingMore) onLoadMore?.();
  }, [sentinelVisible, hasMore, loadingMore, onLoadMore]);

  return { scrollRef, gridRef, sentinelRef, maxHeight };
}
